import { SafraPayCredenciais } from "../types/PagamentoConfig";

/**
 * Tokenização de cartão da SafraPay (Transparent Checkout).
 *
 * A SafraPay não aceita o número do cartão vindo do nosso backend: o cartão precisa ser
 * trocado por um `temporaryCardToken` AQUI, no navegador, pelo SDK deles. É isso que
 * mantém o número do cartão fora dos nossos servidores — e nós fora do escopo de PCI-DSS.
 *
 * O SDK é carregado sob demanda, só quando a adquirente do cliente é SafraPay. Clientes
 * de Pagar.me não baixam nada disso.
 */

const SDK_URL_PROD =
  "https://safrastatic-a.akamaihd.net/safrapay/checkout/prod/safrapay-transparent-v2.0.0.js";
const SDK_URL_DEV =
  "https://safrastatic-a.akamaihd.net/safrapay/checkout/dev/safrapay-transparent-v2.0.0.js";

const SDK_ELEMENT_ID = "safrapay-transparent-sdk";

export interface CartaoTokenizavel {
  /** Só dígitos. */
  numero: string;
  titular: string;
  /** "MM/AAAA". */
  validade: string;
  cvv: string;
  /** Documento do titular, só dígitos. */
  documento: string;
  /** Bandeira no formato que a SafraPay espera (ex.: "visa"). */
  bandeira: string;
}

export interface CartaoTokenizado {
  temporaryCardToken: string;
  cardFirstSixDigits: string;
  cardLastFourDigits: string;
}

interface SafraPayTransparentSDK {
  setCredentials: (credenciais: {
    merchantCredential: string;
    merchantId: string;
  }) => void;
  getCardBrand: (params: {
    bin: string;
    success: (resposta: { brand: string }) => void;
    error: (erro: unknown) => void;
  }) => void;
  createTemporaryCard: (params: {
    card: Record<string, unknown>;
    success: (resposta: { temporaryToken: string }) => void;
    error: (erro: unknown) => void;
  }) => void;
}

declare global {
  interface Window {
    SafraPayTransparent?: SafraPayTransparentSDK;
  }
}

let carregamento: Promise<SafraPayTransparentSDK> | null = null;
let credenciaisAplicadas = false;

/**
 * Aplica as credenciais UMA ÚNICA VEZ por carregamento do SDK.
 *
 * Não é cautela: é contorno de um defeito do SDK. O `setCredentials` deles guarda o CNPJ
 * num objeto de módulo e chama `Object.freeze` nesse objeto ao terminar —
 *
 *   var t = { CNPJ: "", MERCHANT_ID: "" };
 *   function setCredentials(e) { t.CNPJ = e.merchantCredential; ...; Object.freeze(t); }
 *
 * — então a SEGUNDA chamada estoura com "Cannot assign to read only property 'CNPJ'",
 * porque o código do SDK roda em strict mode. Chamar de novo para "garantir" quebra a
 * tokenização inteira.
 *
 * Como consequência, trocar de loja exige recarregar a página. Não acontece em uso normal:
 * a loja vem do backend e não muda dentro de uma sessão.
 */
function aplicarCredenciais(
  sdk: SafraPayTransparentSDK,
  credenciais: SafraPayCredenciais
): void {
  if (credenciaisAplicadas) {
    return;
  }

  sdk.setCredentials({
    merchantCredential: credenciais.merchantCnpj,
    merchantId: credenciais.merchantId,
  });
  credenciaisAplicadas = true;
}

function urlDoSdk(): string {
  // O ambiente do SDK precisa casar com o da loja configurada no backend. Produção usa a
  // loja real; qualquer outro ambiente usa a de homologação.
  return process.env["NODE_ENV"] === "production" ? SDK_URL_PROD : SDK_URL_DEV;
}

/**
 * Carrega o SDK uma única vez por sessão e devolve a instância pronta.
 *
 * A promessa é memoizada: duas aberturas do modal de cartão não baixam o script duas
 * vezes, e chamadas concorrentes esperam o mesmo carregamento.
 */
function carregarSdk(): Promise<SafraPayTransparentSDK> {
  if (window.SafraPayTransparent) {
    return Promise.resolve(window.SafraPayTransparent);
  }

  if (carregamento) {
    return carregamento;
  }

  carregamento = new Promise<SafraPayTransparentSDK>((resolve, reject) => {
    const existente = document.getElementById(SDK_ELEMENT_ID);

    const aoCarregar = () => {
      if (window.SafraPayTransparent) {
        resolve(window.SafraPayTransparent);
      } else {
        reject(
          new Error("SDK da SafraPay carregou sem expor SafraPayTransparent")
        );
      }
    };

    if (existente) {
      existente.addEventListener("load", aoCarregar);
      existente.addEventListener("error", () =>
        reject(new Error("Falha ao carregar o SDK da SafraPay"))
      );
      return;
    }

    const script = document.createElement("script");
    script.id = SDK_ELEMENT_ID;
    script.src = urlDoSdk();
    script.async = true;
    script.onload = aoCarregar;
    script.onerror = () => {
      // Sem o script não há tokenização e, portanto, não há pagamento. Limpar a memoização
      // permite nova tentativa quando o usuário reabrir o modal — uma falha de rede
      // pontual não pode deixar a tela inutilizável até dar refresh.
      carregamento = null;
      credenciaisAplicadas = false;
      reject(new Error("Falha ao carregar o SDK da SafraPay"));
    };

    document.head.appendChild(script);
  });

  return carregamento;
}

/**
 * Prepara o SDK para tokenizar. Chamar assim que souber que o cliente é SafraPay, para o
 * download não acontecer só no clique de confirmar.
 */
export async function prepararSafraPay(
  credenciais: SafraPayCredenciais
): Promise<void> {
  const sdk = await carregarSdk();
  aplicarCredenciais(sdk, credenciais);
}

/**
 * Bandeira do cartão no vocabulário da SafraPay, a partir do BIN.
 *
 * Só o BIN (6 primeiros dígitos) sai daqui — ele identifica o emissor, não o portador.
 */
function obterBandeiraSafraPay(
  sdk: SafraPayTransparentSDK,
  bin: string
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    sdk.getCardBrand({
      bin,
      success: resposta => {
        if (!resposta?.brand) {
          reject(
            new Error("Não foi possível identificar a bandeira do cartão.")
          );
          return;
        }
        resolve(resposta.brand);
      },
      error: () => {
        reject(
          new Error(
            "Não foi possível identificar a bandeira do cartão. Verifique o número e tente novamente."
          )
        );
      },
    });
  });
}

/**
 * Troca os dados do cartão por um token de uso único.
 *
 * O retorno traz também BIN e últimos 4 dígitos, que o backend repassa ao R2D2 como
 * metadados de conciliação — o número completo morre aqui.
 */
export async function tokenizarCartao(
  cartao: CartaoTokenizavel,
  credenciais: SafraPayCredenciais
): Promise<CartaoTokenizado> {
  const sdk = await carregarSdk();

  // Idempotente por causa do freeze interno do SDK — ver aplicarCredenciais().
  aplicarCredenciais(sdk, credenciais);

  const numeroLimpo = cartao.numero.replace(/\D/g, "");
  const [mes, ano] = cartao.validade.split("/");

  // A bandeira vai no vocabulário da SAFRAPAY, obtida do próprio SDK pelo BIN — não no
  // nosso (`detectCardBrand` devolve "Master", a SafraPay quer "MasterCard").
  //
  // Mandar o nome errado faz a tokenização falhar com "Propriedade 'Card' está ausente",
  // que não tem relação nenhuma com a causa: eles validam a bandeira contra a lista deles
  // e devolvem um erro genérico quando não reconhecem. É por isso que o eGAM também
  // consulta getCardBrand antes de tokenizar.
  const brandSafraPay = await obterBandeiraSafraPay(
    sdk,
    numeroLimpo.slice(0, 6)
  );

  return new Promise<CartaoTokenizado>((resolve, reject) => {
    sdk.createTemporaryCard({
      card: {
        brand: brandSafraPay,
        number: numeroLimpo,
        holderName: cartao.titular,
        holderDocument: cartao.documento.replace(/\D/g, ""),
        expirationMonth: Number(mes),
        expirationYear: Number(ano),
        cvv: cartao.cvv,
      },
      success: resposta => {
        if (!resposta?.temporaryToken) {
          reject(new Error("SafraPay não retornou o token do cartão"));
          return;
        }

        resolve({
          temporaryCardToken: resposta.temporaryToken,
          cardFirstSixDigits: numeroLimpo.slice(0, 6),
          cardLastFourDigits: numeroLimpo.slice(-4),
        });
      },
      error: erro => {
        // O erro do SDK não vai para o usuário (descreve a integração e pode ecoar o que
        // foi enviado), mas PRECISA aparecer no console: a mensagem amigável sozinha
        // esconde a causa, e o `error` do SDK é a única pista quando a SafraPay recusa —
        // ela devolve coisas como "Propriedade 'Card' está ausente" para bandeira não
        // reconhecida, que não tem relação com o texto.
        //
        // Atenção ao ler: o callback de erro do SDK é chamado com um objeto malformado
        // quando a resposta não é 200 (eles usam `r.json()` sem await), e pode disparar
        // duas vezes. O `traceKey` da resposta HTTP é o que a SafraPay rastreia.
        // eslint-disable-next-line no-console
        console.error("[SafraPay] createTemporaryCard falhou:", erro);

        reject(
          new Error(
            "Não foi possível validar o cartão. Confira os dados e tente novamente."
          )
        );
      },
    });
  });
}

/**
 * `sessionId` do SDK de antifraude, quando ele estiver presente na página.
 *
 * Opcional por desenho: o antifraude é carregado pelo eGAM em algumas telas e pode não
 * existir aqui. Ausente, o backend simplesmente não envia o campo.
 */
export function obterSessionIdAntifraude(): string | undefined {
  const antifraude = (
    window as unknown as { SafraPayAntifraud?: { getSessionId?: () => string } }
  ).SafraPayAntifraud;

  try {
    return antifraude?.getSessionId?.() || undefined;
  } catch {
    return undefined;
  }
}
